import { useCurrentOrganization } from './api/useOrganizations';
import type { SubscriptionTier } from '../lib/tiers';

export interface TrialStatus {
  /** True when a trial period is currently active (not yet expired). */
  isTrialActive: boolean;
  /** Tier being trialled, or null when no trial has been started. */
  trialTier: SubscriptionTier | null;
  /** When the active trial expires, or null when no trial is set. */
  trialEndsAt: Date | null;
  /** Whole days remaining in the trial. 0 if expired or no trial. */
  daysRemaining: number;
  /** True once the org has consumed its one-shot trial. */
  trialUsed: boolean;
}

/**
 * Derives trial state from the cached current-organization payload.
 *
 * Backed by the existing useCurrentOrganization query so we avoid
 * duplicate /organizations/current fetches.
 */
export function useTrialStatus(): TrialStatus {
  const { data } = useCurrentOrganization();

  const trialTier = (data?.trial_tier ?? null) as SubscriptionTier | null;
  const trialEndsAt = data?.trial_ends_at ? new Date(data.trial_ends_at) : null;
  const isTrialActive =
    !!trialEndsAt && trialEndsAt.getTime() > Date.now();
  const daysRemaining =
    isTrialActive && trialEndsAt
      ? Math.max(
          0,
          Math.ceil((trialEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
        )
      : 0;

  return {
    isTrialActive,
    trialTier,
    trialEndsAt,
    daysRemaining,
    trialUsed: data?.trial_used ?? false,
  };
}
